<template>
  <div class="task-card" :class="{ disabled, focused }" @click="handleClick">
    <div
      v-if="icon"
      class="task-icon-container"
      :class="{ 'fixed-size': fixedIconSize }"
    >
      <AppIcon :name="icon" :size="iconSize" />
    </div>
    <div class="task-content">
      <span v-if="title" class="task-title text-align-left">{{ title }}</span>
      <span class="task-description text-align-left">{{ description }}</span>
      <span v-if="subtitle" class="task-description text-align-left font-bold">
        {{ subtitle }}
      </span>
    </div>
    <div v-if="$slots.actions" class="task-action-container" @click.stop>
      <slot name="actions" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import AppIcon from '@/components/common/app-icon/AppIcon.vue';
import { IconName, IconSize } from '@/models/icon-types';

const emit = defineEmits(['click']);

interface Props {
  icon?: IconName;
  iconSize?: IconSize;
  title?: string;
  description: string;
  disabled?: boolean;
  fixedIconSize?: boolean;
  subtitle?: string;
  focused?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  iconSize: IconSize.HeadlineL,
  disabled: false,
  fixedIconSize: true
});

function handleClick(event: MouseEvent) {
  if (props.disabled) {
    event.stopPropagation();
    return;
  }
  emit('click');
}
</script>

<style scoped lang="scss">
.task-card {
  display: flex;
  flex-direction: row;
  gap: $space-3;
  align-items: flex-start;
  border: $border;
  border-radius: $radius-4;
  padding: $space-2 $space-3;
  cursor: pointer;
  background-color: $color-neutral-0;
  transition: box-shadow 0.2s ease;
  margin-bottom: $space-1;
  font-size: $body-s-font-size;
}

.focused {
  border: 2px solid $border-color-focus;
}

.task-title {
  font-weight: $fw-medium;
  font-size: $body-m-font-size;
  line-height: $body-m-line-height;
}

.task-description {
  font-weight: $fw-regular;
  font-size: $body-s-font-size;
  line-height: $body-s-line-height;
}

.task-card:not(.disabled):hover {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  background-color: $color-neutral-100;
  border-color: $border-color-hover;
}

.task-card.disabled {
  cursor: not-allowed;
  color: $color-text-disabled;
}

.task-icon-container {
  display: flex;
  align-items: center;

  .fixed-size {
    width: $icon-container-size;
    height: $icon-container-size;
  }
}

.task-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.task-action-container {
  margin-left: auto;
}

// Styles for big screens
@media (min-height: $small-screen-height) {
  .task-card {
    padding: $space-3 $space-4;
  }

  .task-title {
    font-size: $headline-xs-font-size;
    line-height: $headline-xs-line-height;
  }

  .task-description {
    font-size: $body-m-font-size;
    line-height: $body-m-line-height;
  }
}
</style>
